import { useState } from "react";
import { toast } from "sonner";
import { Briefcase, CalendarClock, HeartHandshake, Loader2, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { OPENINGS } from "@/data/content";

const PERKS = [
  { icon: HeartHandshake, title: "Work that feels good", text: "Your job is literally brightening someone's week — tea, walks, stories and laughter." },
  { icon: CalendarClock, title: "Hours that fit your life", text: "Choose your days and patch of Glasgow. Perfect around family, studies or semi-retirement." },
  { icon: Briefcase, title: "Properly supported", text: "Competitive hourly rate, mileage paid, thoughtful client matching and a team who know every client by name." },
];

const EMPTY = { name: "", email: "", phone: "", role: "", pvg: "", availability: "", message: "" };

const ApplicationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/careers", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        role: form.role,
        pvg: form.pvg || null,
        availability: form.availability || null,
        message: form.message || null,
      });
      toast.success(data.message);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const fieldClass = "h-12 rounded-xl border-sand bg-white/80 text-ink placeholder:text-pebble/50 focus-visible:ring-sage";

  return (
    <form data-testid="careers-application-form" onSubmit={submit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="careers-name" className="mb-2 block text-sm font-semibold text-forest">Your name *</label>
          <Input id="careers-name" data-testid="careers-name-input" required minLength={2} value={form.name} onChange={set("name")} className={fieldClass} />
        </div>
        <div>
          <label htmlFor="careers-email" className="mb-2 block text-sm font-semibold text-forest">Email address *</label>
          <Input id="careers-email" data-testid="careers-email-input" required type="email" value={form.email} onChange={set("email")} className={fieldClass} />
        </div>
        <div>
          <label htmlFor="careers-phone" className="mb-2 block text-sm font-semibold text-forest">Phone</label>
          <Input id="careers-phone" data-testid="careers-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={fieldClass} />
        </div>
        <div>
          <label htmlFor="careers-role" className="mb-2 block text-sm font-semibold text-forest">Role you're interested in *</label>
          <select id="careers-role" data-testid="careers-role-select" required value={form.role} onChange={set("role")} className="h-12 w-full rounded-xl border border-sand bg-white/80 px-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage">
            <option value="">Choose a role…</option>
            {OPENINGS.map((o) => (
              <option key={o.title + o.location} value={`${o.title} — ${o.location}`}>{o.title} — {o.location}</option>
            ))}
            <option value="General interest">General interest / future openings</option>
          </select>
        </div>
        <div>
          <label htmlFor="careers-pvg" className="mb-2 block text-sm font-semibold text-forest">PVG status *</label>
          <select id="careers-pvg" data-testid="careers-pvg-select" required value={form.pvg} onChange={set("pvg")} className="h-12 w-full rounded-xl border border-sand bg-white/80 px-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage">
            <option value="">Select…</option>
            <option value="I hold a valid PVG (Scheme member)">I hold a valid PVG (Scheme member)</option>
            <option value="Happy to apply for PVG through Brightside">Happy to apply for PVG through Brightside</option>
          </select>
        </div>
        <div>
          <label htmlFor="careers-availability" className="mb-2 block text-sm font-semibold text-forest">Availability</label>
          <Input id="careers-availability" data-testid="careers-availability-input" value={form.availability} onChange={set("availability")} placeholder="e.g. Weekday mornings, Saturdays" className={fieldClass} />
        </div>
      </div>
      <div>
        <label htmlFor="careers-message" className="mb-2 block text-sm font-semibold text-forest">Tell us why you'd make a lovely befriender</label>
        <Textarea id="careers-message" data-testid="careers-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Experience is welcome but not essential — warmth, patience and reliability matter more." className="rounded-xl border-sand bg-white/80 text-ink placeholder:text-pebble/50 focus-visible:ring-sage" />
      </div>
      <Button data-testid="careers-submit-button" type="submit" disabled={sending} className="w-full rounded-full bg-forest py-6 text-base text-linen hover:bg-terracotta sm:w-auto sm:px-10">
        {sending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>) : "Apply to be a befriender"}
      </Button>
    </form>
  );
};

export default function Careers() {
  return (
    <>
      <Seo
        title="Careers — become a companion befriender in Glasgow"
        siteName="Brightside Befriending Services"
        description="Join Brightside as a companion befriender in Glasgow. Flexible, part-time hours; competitive rate and mileage; full PVG support. Warmth matters more than experience."
      />
      <PageHero
        eyebrow="Careers at Brightside"
        lines={["Fancy a job that's", "mostly cups of tea", "and good chat?"]}
        text="We're building a small team of associate befrienders across Glasgow. If you're warm, patient and reliable, we'd love to hear from you."
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="careers-perks">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-3">
          {PERKS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.1}>
              <article className="h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(28,56,41,0.12)]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-tint text-sage">
                  <p.icon className="h-5 w-5" />
                </span>
                <h2 className="mt-6 font-serif text-xl text-forest">{p.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-pebble">{p.text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white/50 px-4 py-24 sm:px-6 lg:px-8" data-testid="careers-openings">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Current openings" title="Where we need friendly faces" />
          <div className="mt-12 space-y-4">
            {OPENINGS.map((o, i) => (
              <Reveal key={o.title + o.location} delay={i * 0.08}>
                <article data-testid={`opening-${i}`} className="flex flex-col justify-between gap-4 rounded-3xl border border-sand bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(28,56,41,0.1)] sm:flex-row sm:items-center sm:p-8">
                  <div>
                    <h3 className="font-serif text-xl text-forest">{o.title}</h3>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-pebble">
                      <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-terracotta" /> {o.location}</span>
                      <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5 text-terracotta" /> {o.type}</span>
                    </p>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-pebble">{o.blurb}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-sage-tint px-4 py-2 text-xs font-semibold uppercase tracking-widest text-forest">Now hiring</span>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8" data-testid="careers-apply-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2">
          <SectionHeading
            eyebrow="Apply now"
            title="Two minutes to introduce yourself"
            text="No CV gymnastics needed at this stage. Tell us who you are and when you're free — if it feels like a fit, we'll invite you for a coffee and a chat, then guide you through PVG. One thing to know: befrienders provide company and light practical help — never personal care, medication or nursing. Prefer to talk first? Job seekers can call 07761 329702 on weekday evenings from 5:30pm, or weekends 9am–8pm."
          />
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-sand bg-white p-8 shadow-[0_16px_40px_rgba(28,56,41,0.06)] sm:p-10">
              <ApplicationForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
